import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApiClient } from "~/hooks/use-api-client";
import { logger } from "~/lib/logger";

type BatchScope = "generate-all" | "generate-missing" | "part-ids";

interface BatchGeneratePartImagesVariables {
    sessionId?: string; // AIDEV-NOTE: Optional — not needed for "generate-all" scope (e.g., from settings page)
    clientId?: string;
    scope?: BatchScope; // default: part-ids when partIds provided; otherwise generate-missing
    partIds?: string[]; // required when scope === part-ids
}

interface BatchGenerateEnqueueResponse {
    success: boolean;
    queued: boolean;
    scope: BatchScope;
    clientId: string;
    totalRequested: number | null;
}

export function useBatchGeneratePartImages() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation<
        BatchGenerateEnqueueResponse,
        Error,
        BatchGeneratePartImagesVariables
    >({
        mutationFn: async ({ partIds, clientId, scope }) => {
            const isPartIdsScope = Array.isArray(partIds) && partIds.length > 0;
            const effectiveScope: BatchScope = isPartIdsScope
                ? "part-ids"
                : scope ?? "generate-missing";

            const payload: Record<string, unknown> = {
                clientId,
                scope: effectiveScope,
            };
            if (effectiveScope === "part-ids") {
                payload.partIds = partIds;
            }

            const response = await apiClient.post(
                `/part/batch-generate-images`,
                payload,
            );

            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                } catch {}
                const errorMessage =
                    errorData?.error ||
                    `HTTP error! status: ${response.status}`;
                throw new Error(errorMessage);
            }

            return response.json() as Promise<BatchGenerateEnqueueResponse>;
        },
        onSuccess: (response, variables) => {
            const { sessionId } = variables;

            logger
                .withMetadata({
                    scope: response.scope,
                    clientId: response.clientId,
                    totalRequested: response.totalRequested,
                })
                .info("Batch image generation enqueued");

            // AIDEV-NOTE: Store pending flag in sessionStorage so /parts shows the monitor
            // immediately even before Inngest creates part_image records.
            // React Query cache doesn't persist across Remix page navigations.
            try {
                sessionStorage.setItem(
                    "batchGeneratePending",
                    JSON.stringify({
                        clientId: response.clientId,
                        timestamp: Date.now(),
                    }),
                );
            } catch {
                // sessionStorage may be unavailable (SSR, private browsing)
            }

            // Invalidate session data; images will arrive asynchronously
            if (sessionId) {
                queryClient.invalidateQueries({
                    queryKey: ["session", sessionId],
                });
                queryClient.invalidateQueries({
                    queryKey: ["session-images", sessionId],
                });
            }
        },
        onError: (error) => {
            logger
                .withError(error)
                .error("Failed to enqueue batch image generation");
        },
    });
}
